import React, { useEffect, useState, useContext } from "react";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import { Context } from "../store/appContext";
import UserTable from "../components/UserTable.jsx";
import UserForm from "../components/UserForm.jsx";
import getApiBaseUrl from "../utils/api";
import "../styles/layout.css";

const API_URL = getApiBaseUrl();

const ROLE_LABELS = {
  user: "Invitado",
  admin: "Administrador",
  tecnico: "Técnico",
  scanner: "Operador escaneo",
};

const UserManagement = () => {
  const { store, actions } = useContext(Context);

  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [userToReset, setUserToReset] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);

  const getUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        headers: {
          Authorization: "Bearer " + store.token,
        },
      });

      const error = await actions.handleApiError(response);
      if (error) return;

      const data = await response.json();

      if (response.ok) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error(error);
      actions.showToast("Error cargando usuarios", "error");
    }
  };

  const deactivateUser = (id) => {
    const user = users.find((u) => u.id === id);

    if (!user) return;

    if (user.username === store.username) {
      actions.showToast(
        "Un administrador no puede desactivarse a sí mismo",
        "warning",
      );
      return;
    }

    setUserToDeactivate(user);
  };

  const reactivateUser = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${id}/activate`, {
        method: "PATCH",
        headers: {
          Authorization: "Bearer " + store.token,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user.id === id ? { ...user, ...data } : user,
          ),
        );
        actions.showToast("Usuario reactivado", "success");
      } else {
        if (response.status === 401 || response.status === 403) {
          await actions.handleApiError(response);
          return;
        }

        actions.showToast(data?.msg || "Error reactivando usuario", "error");
      }
    } catch (error) {
      console.error(error);
      actions.showToast("Error reactivando usuario", "error");
    }
  };

  const confirmDeactivate = async () => {
    if (!userToDeactivate) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${userToDeactivate.id}`, {
        method: "DELETE",
        headers: {
          Authorization: "Bearer " + store.token,
        },
      });

      const error = await actions.handleApiError(response);
      if (error) return;

      if (response.ok) {
        setUsers(
          users.map((user) =>
            user.id === userToDeactivate.id
              ? { ...user, is_active: false }
              : user,
          ),
        );
        actions.showToast("Usuario desactivado", "success");
      }
    } catch (error) {
      console.error(error);
      actions.showToast("Error desactivando usuario", "error");
    }

    setUserToDeactivate(null);
  };

  const requestChangeRole = (id, newRole) => {
    const user = users.find((u) => u.id === id);
    if (!user || user.role === newRole) return;
    setPendingRoleChange({ userId: id, username: user.username, currentRole: user.role, newRole });
  };

  const confirmChangeRole = async () => {
    if (!pendingRoleChange) return;
    const { userId, newRole } = pendingRoleChange;
    setPendingRoleChange(null);

    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + store.token,
        },
        body: JSON.stringify({ role: newRole }),
      });

      const error = await actions.handleApiError(response);
      if (error) return;

      if (response.ok) {
        actions.showToast("Rol actualizado", "success");
        getUsers();
      }
    } catch (error) {
      console.error(error);
      actions.showToast("Error actualizando rol", "error");
    }
  };

  const openResetPassword = (user) => {
    setUserToReset(user);
    setResetPasswordValue("");
    setShowResetPassword(false);
  };

  const closeResetPassword = () => {
    setUserToReset(null);
    setResetPasswordValue("");
    setShowResetPassword(false);
  };

  const confirmResetPassword = async (e) => {
    e.preventDefault();

    if (!userToReset || !resetPasswordValue) {
      actions.showToast("La nueva contraseña es obligatoria", "error");
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/users/${userToReset.id}/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + store.token,
          },
          body: JSON.stringify({ new_password: resetPasswordValue }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user.id === data.id ? { ...user, ...data } : user,
          ),
        );
        actions.showToast("Contraseña reseteada", "success");
        closeResetPassword();
      } else {
        if (response.status === 401 || response.status === 403) {
          await actions.handleApiError(response);
          return;
        }

        actions.showToast(data?.msg || "Error reseteando contraseña", "error");
      }
    } catch (error) {
      console.error(error);
      actions.showToast("Error reseteando contraseña", "error");
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    if (statusFilter === "active") return user.is_active !== false;
    if (statusFilter === "inactive") return user.is_active === false;
    return true;
  });

  return (
    <div className="container mt-4">
      <h2>Gestión de usuarios</h2>

      <div className="mb-3">
        <button
          className="btn btn-success"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cerrar" : "Crear usuario"}
        </button>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <UserForm onUserCreated={getUsers} />
          </div>
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="user-status-filter" className="form-label">
          Estado
        </label>
        <select
          id="user-status-filter"
          className="select select-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
        </select>
      </div>

      <UserTable
        users={filteredUsers}
        deactivateUser={deactivateUser}
        reactivateUser={reactivateUser}
        changeRole={requestChangeRole}
        resetPassword={openResetPassword}
      />

      {userToReset && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <form onSubmit={confirmResetPassword}>
                <div className="modal-header">
                  <h5 className="modal-title">Resetear contraseña</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Cerrar"
                    onClick={closeResetPassword}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Usuario: <strong>{userToReset.username}</strong>
                  </p>
                  <label className="form-label">Nueva contraseña</label>
                  <div className="input-group">
                    <input
                      type={showResetPassword ? "text" : "password"}
                      className="form-control"
                      autoComplete="new-password"
                      value={resetPasswordValue}
                      onChange={(e) => setResetPasswordValue(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      aria-label={
                        showResetPassword
                          ? "Ocultar contraseña"
                          : "Mostrar contraseña"
                      }
                    >
                      <i
                        className={`bi ${
                          showResetPassword ? "bi-eye-slash" : "bi-eye"
                        }`}
                      ></i>
                    </button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeResetPassword}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Resetear
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(userToDeactivate)}
        title="Desactivar usuario"
        message={`Desactivar a «${userToDeactivate?.username}»: no podrá iniciar sesión, pero su historial se conservará.`}
        confirmLabel="Desactivar"
        cancelLabel="Cancelar"
        danger
        onConfirm={confirmDeactivate}
        onCancel={() => setUserToDeactivate(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingRoleChange)}
        title="Cambiar rol de usuario"
        message={`Cambiar el rol de «${pendingRoleChange?.username}» de ${ROLE_LABELS[pendingRoleChange?.currentRole] ?? pendingRoleChange?.currentRole} a ${ROLE_LABELS[pendingRoleChange?.newRole] ?? pendingRoleChange?.newRole}. El cambio tiene efecto inmediato.`}
        confirmLabel="Confirmar cambio"
        cancelLabel="Cancelar"
        onConfirm={confirmChangeRole}
        onCancel={() => setPendingRoleChange(null)}
      />
    </div>
  );
};

export default UserManagement;
